import { useCallback, useState } from 'react';
import { User } from 'domains/User';
import { IUserRepository } from 'repository/U006/IUserRepository';
import { UserRepository } from 'repository/U006/UserRepository';

type UpdateUserRequest = {
  user: User;
  revision: number;
};
type UpdateUserResponse = {
  status: string;
  msgid: string;
};

type UpdateUserDoRequest = (param: {
  request: UpdateUserRequest;
  onSuccess?: (response: UpdateUserResponse) => void;
  onError?: (err: Error) => void;
  cancellation?: AbortSignal;
}) => void;

export function useUpdateUser(): [UpdateUserDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const updateUser = async (request: UpdateUserRequest, cancellation: AbortSignal) => {
    const repository: IUserRepository = new UserRepository();

    return await repository.update(request.user, request.revision, cancellation);
  };

  const doRequest = useCallback<UpdateUserDoRequest>(
    async ({ request, cancellation, onSuccess, onError }) => {
      const controller = new AbortController();
      const cancelSignal = cancellation ?? controller.signal;
      try {
        setLoading(true);
        setError(null);
        const user = await updateUser(request, cancelSignal);
        onSuccess?.(user);
      } catch (err) {
        console.error(err);
        const flooredError = err instanceof Error ? err : new Error(`unknown error. ${err}`);
        onError?.(flooredError);
        setError(flooredError);
      } finally {
        setLoading(false);
      }
    },
    [updateUser, setLoading, setError]
  );

  return [doRequest, loading, err];
}
